import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  Save,
  Key,
  Package,
  Users,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  BarChart3,
  Plus,
  X,
} from 'lucide-react';
import {
  useLender,
  useLenderProducts,
  useLenderUsers,
  useUpdateLender,
  useBlockLender,
  useUnblockLender,
  useDeleteLender,
} from '../../hooks/useLenders';
import { useUrnResolver } from '../../hooks/useUrnResolver';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import { formatDate } from '../../utils/date';

export default function LenderDetailPage() {
  const { urn } = useParams<{ urn: string }>();
  const decodedUrn = decodeURIComponent(urn!);
  const navigate = useNavigate();
  const { data: lender, isLoading, error } = useLender(decodedUrn);
  const { data: lenderProducts } = useLenderProducts(decodedUrn);
  const { data: lenderUsers } = useLenderUsers(decodedUrn);
  const updateLender = useUpdateLender();
  const blockLender = useBlockLender();
  const unblockLender = useUnblockLender();
  const deleteLender = useDeleteLender();
  const { resolveProductUrn } = useUrnResolver();

  const { register, handleSubmit } = useForm();

  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmUrn, setDeleteConfirmUrn] = useState('');

  // Credential form state
  const [showCredForm, setShowCredForm] = useState(false);
  const [credForm, setCredForm] = useState({ name: '', key: '', secret: '' });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !lender) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 dark:text-red-400 mb-2">Failed to load lender</p>
        <button onClick={() => navigate('/lenders')} className="btn-secondary">Back to Lenders</button>
      </div>
    );
  }

  const isActive = (lender.status || 'active') === 'active';

  const onSubmit = (values: Record<string, unknown>) => {
    updateLender.mutate({ urn: decodedUrn, data: values });
  };

  const handleToggleStatus = () => {
    if (isActive) {
      blockLender.mutate(decodedUrn);
    } else {
      unblockLender.mutate(decodedUrn);
    }
  };

  const handleDelete = () => {
    deleteLender.mutate(
      { urn: decodedUrn, confirmUrn: deleteConfirmUrn },
      {
        onSuccess: () => {
          navigate('/lenders');
        },
      },
    );
  };

  const handleAddCredential = () => {
    if (!credForm.name || !credForm.key || !credForm.secret) return;
    const existingCreds = lender.credentials || [];
    const updatedCreds = [
      ...existingCreds.map((c) => ({ name: c.name || c.key, key: c.key, secret: c.secret || '' })),
      { name: credForm.name, key: credForm.key, secret: credForm.secret },
    ];
    updateLender.mutate(
      { urn: decodedUrn, data: { credentials: updatedCreds } },
      {
        onSuccess: () => {
          setShowCredForm(false);
          setCredForm({ name: '', key: '', secret: '' });
        },
      },
    );
  };

  const handleUpdateCredentialSecret = (credKey: string, newSecret: string) => {
    const existingCreds = lender.credentials || [];
    const updatedCreds = existingCreds.map((c) => ({
      name: c.name || c.key,
      key: c.key,
      secret: c.key === credKey ? newSecret : (c.secret || ''),
    }));
    updateLender.mutate({ urn: decodedUrn, data: { credentials: updatedCreds } });
  };

  return (
    <div>
      <PageHeader
        title={lender.displayName || lender.name}
        description={lender.urn}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={lender.status || 'active'} />
            <button
              onClick={() => navigate(`/lenders/${encodeURIComponent(decodedUrn)}/reports`)}
              className="btn-secondary gap-1"
            >
              <BarChart3 className="h-4 w-4" />
              Reports
            </button>
            <button onClick={handleToggleStatus} className="btn-secondary gap-1">
              {isActive ? (
                <>
                  <ShieldAlert className="h-4 w-4 text-red-500" />
                  Block
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 text-green-500" />
                  Unblock
                </>
              )}
            </button>
            <button onClick={() => navigate('/lenders')} className="btn-secondary gap-1">
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
        }
      />

      <div className="max-w-4xl space-y-4 lg:space-y-6">
        {/* Lender Info */}
        <form onSubmit={handleSubmit(onSubmit)} className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Lender Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 mb-4">
            <div>
              <label className="label">Display Name</label>
              <input
                type="text"
                className="input"
                defaultValue={lender.displayName}
                {...register('displayName')}
              />
            </div>
            <div>
              <label className="label">Name</label>
              <input type="text" className="input bg-gray-50 dark:bg-gray-700" value={lender.name} disabled />
            </div>
            <div>
              <label className="label">Created</label>
              <input type="text" className="input bg-gray-50 dark:bg-gray-700" value={formatDate(lender.creationDate)} disabled />
            </div>
            <div>
              <label className="label">Updated</label>
              <input type="text" className="input bg-gray-50 dark:bg-gray-700" value={formatDate(lender.modificationDate)} disabled />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={updateLender.isPending} className="btn-primary gap-1">
              <Save className="h-4 w-4" />
              Save Changes
            </button>
          </div>
        </form>

        {/* Products */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-gray-400" />
            Products ({lender.products?.length || 0})
          </h2>
          {(!lender.products || lender.products.length === 0) ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No products assigned.</p>
          ) : (
            <div className="space-y-1">
              {lender.products.map((product, idx) => {
                const productUrn = typeof product === 'string' ? product : product.urn;
                const productName = typeof product === 'string' ? undefined : product.name;
                return (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <span className="text-sm text-gray-700 dark:text-gray-200" title={productUrn}>
                      {productName || resolveProductUrn(productUrn)}
                    </span>
                    <span className="font-mono text-[10px] text-gray-400 dark:text-gray-500 truncate hidden sm:block">
                      {productUrn}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {lenderProducts && lenderProducts.length > 0 && lenderProducts !== lender.products && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Resolved Products</h3>
              <div className="space-y-1">
                {lenderProducts.map((p, idx) => (
                  <div key={idx} className="px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-xs font-mono text-blue-700 dark:text-blue-300">
                    {typeof p === 'string' ? p : JSON.stringify(p)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Credentials */}
        <div className="card p-4 lg:p-6">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Key className="h-5 w-5 text-gray-400" />
              Credentials ({lender.credentials?.length || 0})
            </h2>
            <button
              onClick={() => setShowCredForm(!showCredForm)}
              className="btn-secondary gap-1 text-xs"
            >
              {showCredForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              {showCredForm ? 'Cancel' : 'Add'}
            </button>
          </div>

          {showCredForm && (
            <div className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="label">Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g. meridianlink"
                    value={credForm.name}
                    onChange={(e) => setCredForm({ ...credForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Key</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g. meridianlink"
                    value={credForm.key}
                    onChange={(e) => setCredForm({ ...credForm, key: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Secret</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="Enter secret"
                    value={credForm.secret}
                    onChange={(e) => setCredForm({ ...credForm, secret: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleAddCredential}
                  disabled={!credForm.name || !credForm.key || !credForm.secret || updateLender.isPending}
                  className="btn-primary gap-1 text-xs"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save Credential
                </button>
              </div>
            </div>
          )}

          {(!lender.credentials || lender.credentials.length === 0) ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No credentials configured.</p>
          ) : (
            <div className="space-y-2">
              {lender.credentials.map((cred, idx) => (
                <CredentialRow
                  key={idx}
                  credKey={cred.key}
                  credName={cred.name || cred.key}
                  onUpdateSecret={(newSecret) => handleUpdateCredentialSecret(cred.key, newSecret)}
                  isPending={updateLender.isPending}
                />
              ))}
            </div>
          )}
        </div>

        {/* Users */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-gray-400" />
            Users ({lenderUsers?.length || 0})
          </h2>
          {(!lenderUsers || lenderUsers.length === 0) ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No users associated with this lender.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Privileges</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {lenderUsers.map((user) => (
                    <tr
                      key={user.urn}
                      onClick={() => navigate(`/users/${encodeURIComponent(user.urn)}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">{user.email}</td>
                      <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{user.privileges?.length || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Integrations */}
        {lender.integrations && Object.keys(lender.integrations).length > 0 && (
          <div className="card p-4 lg:p-6">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Integrations</h2>
            <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-auto max-h-60 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
              {JSON.stringify(lender.integrations, null, 2)}
            </pre>
          </div>
        )}

        {/* Danger Zone */}
        <div className="card p-4 lg:p-6 border-red-200 dark:border-red-900/50">
          <h2 className="text-base lg:text-lg font-semibold text-red-600 dark:text-red-400 mb-3 lg:mb-4 flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Danger Zone
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Permanently delete this lender. This action cannot be undone.
          </p>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="btn-danger gap-1"
          >
            <Trash2 className="h-4 w-4" />
            Delete Lender
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => { setShowDeleteDialog(false); setDeleteConfirmUrn(''); }} />
          <div className="card relative z-10 max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Lender</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  This will permanently delete <strong>{lender.displayName || lender.name}</strong>. To confirm, type the full URN below:
                </p>
              </div>
            </div>
            <div className="mb-2">
              <p className="font-mono text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 rounded px-2 py-1 mb-2 select-all">
                {lender.urn}
              </p>
              <input
                type="text"
                className="input"
                placeholder="Type the URN to confirm"
                value={deleteConfirmUrn}
                onChange={(e) => setDeleteConfirmUrn(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setShowDeleteDialog(false); setDeleteConfirmUrn(''); }} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteConfirmUrn !== lender.urn || deleteLender.isPending}
                className="btn-danger"
              >
                {deleteLender.isPending ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
            {deleteLender.isError && (
              <p className="text-sm text-red-500 mt-2">
                Failed: {(deleteLender.error as Error).message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Inline component for a single credential row with edit-secret functionality */
function CredentialRow({
  credKey,
  credName,
  onUpdateSecret,
  isPending,
}: {
  credKey: string;
  credName: string;
  onUpdateSecret: (secret: string) => void;
  isPending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [newSecret, setNewSecret] = useState('');

  const handleSave = () => {
    if (!newSecret) return;
    onUpdateSecret(newSecret);
    setEditing(false);
    setNewSecret('');
  };

  return (
    <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{credName}</span>
          <span className="font-mono text-[10px] text-gray-400 dark:text-gray-500">key: {credKey}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-mono">
            {editing ? '' : '••••••••'}
          </span>
          <button
            onClick={() => setEditing(!editing)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {editing ? 'Cancel' : 'Update secret'}
          </button>
        </div>
      </div>
      {editing && (
        <div className="flex items-center gap-2 mt-2">
          <input
            type="password"
            className="input text-xs flex-1"
            placeholder="Enter new secret"
            value={newSecret}
            onChange={(e) => setNewSecret(e.target.value)}
          />
          <button
            onClick={handleSave}
            disabled={!newSecret || isPending}
            className="btn-primary text-xs px-3 py-1.5"
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}
